import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClient, useInvoices, useQuotes, useUpdateClient, useDeleteClient, useKboLookup } from '@/hooks/useApi';
import toast from 'react-hot-toast';
import PageSkeleton from '@/components/PageSkeleton';
import type { KboEnterprise } from '@/types';

const LANGUAGES = ['en', 'fr', 'nl', 'es'] as const;

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: client, isLoading } = useClient(id!);
  const { data: invoices } = useInvoices({ clientId: id, limit: 10 });
  const { data: quotes } = useQuotes({ clientId: id, limit: 10 });
  const updateClient = useUpdateClient();
  const deleteClient = useDeleteClient();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', vatNumber: '', type: 'company',
    countryCode: 'BE', address: '', city: '', postalCode: '', language: 'en', notes: '',
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vatLookup, setVatLookup] = useState('');
  const { data: kboData, isFetching: kboLoading } = useKboLookup(vatLookup);

  function applyKboData(kbo: KboEnterprise) {
    const street = [kbo.streetFr || kbo.streetNl, kbo.houseNumber, kbo.box].filter(Boolean).join(' ');
    setForm({
      ...form,
      name: kbo.nameFr || kbo.nameNl || kbo.commercialName || form.name,
      vatNumber: kbo.vatNumber,
      email: kbo.email || form.email,
      phone: kbo.phone || form.phone,
      address: street || form.address,
      city: kbo.municipalityFr || kbo.municipalityNl || form.city,
      postalCode: kbo.zipcode || form.postalCode,
      countryCode: 'BE',
      type: kbo.typeOfEnterprise === '1' ? 'individual' : 'company',
    });
    toast.success(t('kbo.dataApplied'));
  }

  function startEditing() {
    if (!client) return;
    setForm({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      vatNumber: client.vatNumber || '',
      type: client.type || 'company',
      countryCode: client.countryCode || 'BE',
      address: client.address || '',
      city: client.city || '',
      postalCode: client.postalCode || '',
      language: client.defaultLanguage || 'en',
      notes: client.notes || '',
    });
    setEditing(true);
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    updateClient.mutate({ id: id!, ...form }, {
      onSuccess: () => { toast.success(t('common.saved')); setEditing(false); },
    });
  }

  function handleDelete() {
    deleteClient.mutate(id!, {
      onSuccess: () => { toast.success(t('common.deleted')); navigate('/clients'); },
    });
  }

  if (isLoading) return <PageSkeleton variant="detail" />;

  if (!client) return <p className="text-gray-400 py-12 text-center">Client not found</p>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600">←</button>
        <h1 className="text-2xl font-bold">{client.name}</h1>
        <span className="badge badge-gray">{t(`clients.types.${client.type}`)}</span>
        <div className="ml-auto flex gap-2">
          {!editing && (
            <>
              <button className="btn btn-secondary text-sm" onClick={startEditing}>{t('common.edit')}</button>
              <button className="btn btn-secondary text-sm text-red-600 hover:text-red-700" onClick={() => setShowDeleteConfirm(true)}>{t('common.delete')}</button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client info / edit form */}
        <div className="space-y-6">
          {editing ? (
            <form onSubmit={handleSave} className="card card-body space-y-4">
              <h3 className="font-semibold text-base">{t('clients.details')}</h3>
              <div>
                <label className="label">{t('clients.name')} *</label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <label className="label">{t('clients.type_label')}</label>
                <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {['company', 'individual', 'government', 'non_profit'].map((tp) => (
                    <option key={tp} value={tp}>{t(`clients.types.${tp}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">{t('clients.email')}</label>
                <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="label">{t('clients.phone')}</label>
                <input type="text" className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="label">{t('clients.vatNumber')}</label>
                <div className="flex gap-2">
                  <input type="text" className="input flex-1" value={form.vatNumber} onChange={(e) => setForm({ ...form, vatNumber: e.target.value })} placeholder="BE0123456789" />
                  <button type="button" className="btn btn-secondary text-xs whitespace-nowrap" disabled={!form.vatNumber || form.vatNumber.length < 10 || kboLoading}
                    onClick={() => setVatLookup(form.vatNumber)}>
                    {kboLoading ? '...' : t('kbo.lookup')}
                  </button>
                </div>
                {kboData && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                    <div className="font-medium text-green-800 mb-1">{kboData.nameFr || kboData.nameNl}</div>
                    <div className="text-green-700 text-xs space-y-0.5 mb-2">
                      {kboData.streetFr && <div>{kboData.streetFr} {kboData.houseNumber}, {kboData.zipcode} {kboData.municipalityFr}</div>}
                      {kboData.activities?.[0] && <div>{t('kbo.activity')}: {kboData.activities[0].descriptionFr || kboData.activities[0].naceCode}</div>}
                    </div>
                    <button type="button" className="btn btn-primary w-full text-sm py-1.5" onClick={() => applyKboData(kboData)}>
                      {t('kbo.useCompany')}
                    </button>
                  </div>
                )}
                {vatLookup && !kboLoading && !kboData && (
                  <p className="text-xs text-gray-400 mt-1">{t('kbo.notFound')}</p>
                )}
              </div>
              <div>
                <label className="label">{t('clients.country')}</label>
                <input type="text" className="input" maxLength={2} value={form.countryCode} onChange={(e) => setForm({ ...form, countryCode: e.target.value.toUpperCase() })} />
              </div>
              <div>
                <label className="label">{t('clients.address')}</label>
                <input type="text" className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">{t('clients.postalCode')}</label>
                  <input type="text" className="input" value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} />
                </div>
                <div>
                  <label className="label">{t('clients.city')}</label>
                  <input type="text" className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="label">{t('clients.language')}</label>
                <select className="input" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                  {LANGUAGES.map((l) => (
                    <option key={l} value={l}>{t(`languages.${l}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">{t('common.notes')}</label>
                <textarea className="input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="btn btn-primary flex-1" disabled={updateClient.isPending}>{t('common.save')}</button>
                <button type="button" className="btn btn-secondary flex-1" onClick={() => setEditing(false)}>{t('common.cancel')}</button>
              </div>
            </form>
          ) : (
            <div className="card card-body space-y-2 text-sm">
              <h3 className="font-semibold text-base mb-2">{t('clients.details')}</h3>
              {client.email && <div><span className="text-gray-500">{t('clients.email')}:</span> <span className="ml-2">{client.email}</span></div>}
              {client.phone && <div><span className="text-gray-500">{t('clients.phone')}:</span> <span className="ml-2">{client.phone}</span></div>}
              {client.vatNumber && <div><span className="text-gray-500">{t('clients.vatNumber')}:</span> <span className="ml-2 font-mono">{client.vatNumber}</span></div>}
              <div><span className="text-gray-500">{t('clients.country')}:</span> <span className="ml-2">{client.country}</span></div>
              {client.address && <div><span className="text-gray-500">{t('clients.address')}:</span> <span className="ml-2">{client.address}</span></div>}
              {(client.postalCode || client.city) && (
                <div><span className="text-gray-500">{t('clients.city')}:</span> <span className="ml-2">{client.postalCode} {client.city}</span></div>
              )}
              {client.defaultLanguage && (
                <div><span className="text-gray-500">{t('clients.language')}:</span> <span className="ml-2">{t(`languages.${client.defaultLanguage}`)}</span></div>
              )}
              {client.notes && (
                <div><span className="text-gray-500">{t('common.notes')}:</span> <span className="ml-2">{client.notes}</span></div>
              )}
            </div>
          )}
        </div>

        {/* Invoices */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h3 className="font-semibold">{t('invoices.title')}</h3>
              <Link to={`/invoices/new?clientId=${id}`} className="text-sm text-primary-600 hover:text-primary-500">
                + {t('invoices.createInvoice')}
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('invoices.number')}</th>
                    <th>{t('invoices.date')}</th>
                    <th className="text-right">{t('common.amount')}</th>
                    <th>{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invoices?.data?.map((inv) => (
                    <tr key={inv.id}>
                      <td><Link to={`/invoices/${inv.id}`} className="text-primary-600 hover:underline">{inv.invoiceNumber}</Link></td>
                      <td className="text-gray-500">{new Date(inv.issueDate).toLocaleDateString()}</td>
                      <td className="text-right font-medium">€{Number(inv.totalAmount).toFixed(2)}</td>
                      <td><span className="badge badge-gray">{t(`invoices.status.${inv.status}`)}</span></td>
                    </tr>
                  ))}
                  {(!invoices?.data || invoices.data.length === 0) && (
                    <tr><td colSpan={4} className="text-center text-gray-400 py-8">{t('common.noResults')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h3 className="font-semibold">{t('quotes.title')}</h3>
              <Link to={`/quotes/new?clientId=${id}`} className="text-sm text-primary-600 hover:text-primary-500">
                + {t('quotes.createQuote')}
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('quotes.number')}</th>
                    <th>{t('quotes.date')}</th>
                    <th className="text-right">{t('common.amount')}</th>
                    <th>{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {quotes?.data?.map((q) => (
                    <tr key={q.id}>
                      <td><Link to={`/quotes/${q.id}`} className="text-primary-600 hover:underline">{q.quoteNumber}</Link></td>
                      <td className="text-gray-500">{new Date(q.issueDate).toLocaleDateString()}</td>
                      <td className="text-right font-medium">€{Number(q.totalAmount).toFixed(2)}</td>
                      <td><span className="badge badge-gray">{t(`quotes.status.${q.status}`)}</span></td>
                    </tr>
                  ))}
                  {(!quotes?.data || quotes.data.length === 0) && (
                    <tr><td colSpan={4} className="text-center text-gray-400 py-8">{t('common.noResults')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="modal-backdrop">
          <div className="card w-full max-w-sm modal-sheet">
            <div className="card-body text-center space-y-4">
              <p className="text-gray-700">{t('common.confirmDelete')}</p>
              <div className="flex gap-2 justify-center">
                <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)}>{t('common.cancel')}</button>
                <button className="btn btn-primary bg-red-600 hover:bg-red-700" onClick={handleDelete} disabled={deleteClient.isPending}>{t('common.delete')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
